import { ReportingSport } from '@frontend/core/lib/domains/reporting/types'
import { defaultSinglePitch as amfPitch } from '@frontend/core/lib/domains/sports/american_football/pitch'
import {
  AmfPosition,
  amfPositionResolver
} from '@frontend/core/lib/domains/sports/positions'
import type { UnitSystem } from '@frontend/core/lib/domains/units/types'
import { getAmfTechnicalMetrics } from './amf-technical'
import { ReportingSportConfig, type ReportingSportData } from './base'
import type { ColumnDef, DashboardChartMetricDef } from './types'

// Backend emits SI (metres, m/s) on AmericanFootballTeamSessionSummaryMetrics,
// same as every other sport. `buildMetrics` converts to the selected unit
// system like any other metric; nothing AMF-specific is needed here.

// Gridiron speed zones, labelled with their mph edges in every unit system
// because the edges belong to the sport, not the viewer's units.
const speedZoneColumns: ColumnDef[] = [
  {
    accessor: 'sprintDistance_m',
    label: 'Sprint >18 mph',
    unitType: 'distance'
  },
  {
    accessor: 'runningDistance_m',
    label: 'Running 12–18 mph',
    unitType: 'distance'
  },
  {
    accessor: 'joggingDistance_m',
    label: 'Jogging 5–12 mph',
    unitType: 'distance'
  }
]

const amfChartMetrics: DashboardChartMetricDef[] = [
  {
    name: 'Distance',
    key: 'totalDistance_m',
    fallbackMax: 10000,
    unitType: 'distance'
  },
  {
    name: 'Distance per min',
    key: 'distancePerMinute_m',
    fallbackMax: 200,
    unitType: 'distance'
  },
  {
    name: 'High Speed Distance',
    key: 'highSpeedRunDistance_m',
    fallbackMax: 2000,
    unitType: 'distance'
  },
  {
    name: 'Sprint Count',
    key: 'sprintCount',
    fallbackMax: 100,
    unitType: 'count'
  },
  {
    name: 'Acceleration Count',
    key: 'highIntensityAccelCount',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Deceleration count',
    key: 'highIntensityDecelCount',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Passes',
    key: 'passesAttempted',
    fallbackMax: 200,
    unitType: 'count'
  },
  {
    name: 'Pass Count - Velocity Band 1 (<40mph)',
    key: 'throwCountLt18ms',
    fallbackMax: 100,
    unitType: 'count'
  },
  {
    name: 'Pass Count - Velocity Band 2 (40 - 50mph)',
    key: 'throwCount18msTo22ms',
    fallbackMax: 100,
    unitType: 'count'
  },
  {
    name: 'Pass Count - Velocity Band 3 (>50mph)',
    key: 'throwCountGt22ms',
    fallbackMax: 100,
    unitType: 'count'
  },
  {
    name: 'Ball Velocity',
    key: 'initialReleaseVelocityAvg_mps',
    fallbackMax: 40,
    unitType: 'speed'
  },
  // Time-to-throw is measured in seconds across every unit system; there
  // is no conversion, it renders at the standard 1 dp. No `unitType` — the
  // unit label ("s") is part of the metric name downstream in the dashboard
  // print.
  {
    name: 'Time to Throw (s)',
    key: 'timeToThrowAvg_s',
    fallbackMax: 5
  }
]

const americanFootballData: ReportingSportData<AmfPosition> = {
  sport: ReportingSport.AmericanFootball,
  displayName: 'American Football',
  emoji: '🏈',
  themeClass: 'sport-amf',
  periodLabel: 'Quarter',
  periodPluralLabel: 'Quarters',
  pitch: {
    sport: amfPitch.sport,
    coordinates: amfPitch.pitch.coordinates ?? {}
  },

  positionFilters: [
    { value: '', label: 'All positions', canonical: [] },
    {
      value: 'QB',
      label: 'Quarterbacks',
      canonical: [AmfPosition.Quarterback]
    },
    {
      value: 'WR',
      label: 'Wide Receivers',
      canonical: [AmfPosition.WideReceiver]
    },
    {
      value: 'ST',
      label: 'Special Teams',
      // The "Special Teams" filter is a grouping — it includes the dedicated
      // ST flag plus the kick/punt/long-snap unit roles.
      canonical: [
        AmfPosition.SpecialTeams,
        AmfPosition.Kicker,
        AmfPosition.Punter,
        AmfPosition.LongSnapper
      ]
    }
  ],

  resolvePosition: amfPositionResolver.resolve,

  positionColors: {
    [AmfPosition.Quarterback]: '#ef4444',
    [AmfPosition.RunningBack]: '#f59e0b',
    [AmfPosition.WideReceiver]: '#3b82f6',
    [AmfPosition.TightEnd]: '#8b5cf6',
    [AmfPosition.OffensiveLine]: '#6b7280',
    [AmfPosition.DefensiveLine]: '#dc2626',
    [AmfPosition.Linebacker]: '#f97316',
    [AmfPosition.Safety]: '#06b6d4',
    [AmfPosition.Cornerback]: '#10b981',
    [AmfPosition.Kicker]: '#a855f7',
    [AmfPosition.Punter]: '#a855f7',
    [AmfPosition.LongSnapper]: '#6b7280',
    [AmfPosition.SpecialTeams]: '#a855f7'
  },

  // Outcome-keyed QB-throw pills. Each throw is bucketed once by outcome
  // priority (Touchdown > Interception > Incomplete > Complete) inside
  // `usePlayVisualization`, so a pick-six lands under `Interception`
  // (from the QB's perspective) and a caught TD lands under `Touchdown`.
  playCategories: [
    {
      category: 'Complete',
      label: 'Complete',
      actionTypes: ['AmericanFootballPass'],
      filterField: 'passingPlayerId',
      outcomeFilter: 'Complete',
      color: '#22c55e'
    },
    {
      category: 'Incomplete',
      label: 'Incomplete',
      actionTypes: ['AmericanFootballPass'],
      filterField: 'passingPlayerId',
      outcomeFilter: 'Incomplete',
      color: '#e5e7eb'
    },
    {
      category: 'Interception',
      label: 'Interception',
      actionTypes: ['AmericanFootballPass'],
      filterField: 'passingPlayerId',
      outcomeFilter: 'Interception',
      color: '#ef4444'
    },
    {
      category: 'Touchdown',
      label: 'Touchdown',
      actionTypes: ['AmericanFootballPass'],
      filterField: 'passingPlayerId',
      outcomeFilter: 'Touchdown',
      color: '#3b82f6'
    }
  ],

  dashboard: {
    summaryMetricKeys: ['totalDistance_m', 'avgMaxSpeed_mps', 'sprintCount'],
    topListKeys: [
      'initialReleaseVelocityAvg_mps',
      'passCompletionPct',
      'timeToThrowAvg_s',
      'catchCompletePercentage'
    ],
    chartMetrics: amfChartMetrics
  },

  playerPerformance: {
    staticColumns: [
      { accessor: 'period', label: 'Quarter' },
      { accessor: 'duration', label: 'Duration' }
    ],
    summaryColumns: [
      { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
      { accessor: 'passCompletionPct', label: 'Complete %' },
      {
        accessor: 'highVelocityThrowCount',
        label: 'High Vel',
        unitType: 'count'
      },
      { accessor: 'successPercentage', label: 'Success %' },
      { accessor: 'technicalLoad', label: 'Tech Load' },
      { accessor: 'totalDistance_m', label: 'Distance', unitType: 'distance' },
      { accessor: 'technicalLoadPerMin', label: 'Load/min' },
      { accessor: 'sprintCount', label: 'Sprints', unitType: 'count' }
    ],
    // Tab order (left → right): QB Pass, Physical, Technical Load.
    // `all`, `wr_route`, `kick`, `punt` stay in the codebase (commented
    // out) so they can be re-enabled once the corresponding stats ship
    // end-to-end — leaving the column mappings in place means the
    // switch-back is a pure uncomment.
    tabGroups: [
      {
        key: 'physical',
        label: 'Physical',
        tabs: [
          {
            key: 'qb_pass',
            label: 'QB Pass',
            columns: [
              { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
              {
                accessor: 'highVelocityThrowCount',
                label: 'High Vel',
                unitType: 'count'
              },
              { accessor: 'passCompletionPct', label: 'Complete %' },
              {
                accessor: 'initialReleaseVelocityAvg_mps',
                label: 'Release Vel',
                unitType: 'speed'
              },
              { accessor: 'spinAvg_rps', label: 'Spin', unitType: 'revs' },
              {
                accessor: 'hangTimeAvg_s',
                label: 'Hang Time',
                unitType: 'time'
              },
              {
                accessor: 'throwDistanceAvg_m',
                label: 'Distance',
                unitType: 'distance'
              },
              {
                accessor: 'airYardsGainedAvg_m',
                label: 'Air Yards',
                unitType: 'distance'
              },
              {
                accessor: 'timeToThrowAvg_s',
                label: 'Time to Throw',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'physical_summary',
            label: 'Physical',
            columns: [
              {
                accessor: 'totalDistance_m',
                label: 'Distance',
                unitType: 'distance'
              },
              ...speedZoneColumns,
              {
                accessor: 'maxSpeed_mps',
                label: 'Max Speed',
                unitType: 'speed'
              },
              {
                accessor: 'highIntensityAccelCount',
                label: 'High Accel Count',
                unitType: 'count'
              },
              {
                accessor: 'highIntensityDecelCount',
                label: 'High Decel Count',
                unitType: 'count'
              }
            ]
          },
          {
            key: 'technical_load',
            label: 'Technical Load',
            columns: [
              { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
              { accessor: 'throwCountPerMin', label: 'Throws/min' },
              {
                accessor: 'throwCountLt15m',
                label: '<15 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount15mTo30m',
                label: '15-30 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount30mTo45m',
                label: '30-45 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountGt45m',
                label: '>45 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountLt18ms',
                label: '<40 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount18msTo22ms',
                label: '40-50 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountGt22ms',
                label: '>50 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              }
            ]
          }
          // Disabled — re-enable once the stats are wired end-to-end.
          // {
          //   key: 'all',
          //   label: 'All',
          //   columns: [
          //     { accessor: 'technicalLoad', label: 'Tech Load' },
          //     { accessor: 'technicalLoadPerMin', label: 'Tech Load/min' },
          //     { accessor: 'successPercentage', label: 'Success %' },
          //     { accessor: 'playerLoad', label: 'Player Load' },
          //     { accessor: 'playerLoadPerMin', label: 'Player Load/min' }
          //   ]
          // },
          // {
          //   key: 'wr_route',
          //   label: 'WR Route',
          //   columns: [
          //     { accessor: 'routeCount', label: 'Routes', unitType: 'count' },
          //     { accessor: 'routePlusPassedToCount', label: 'Route + Pass', unitType: 'count' },
          //     { accessor: 'catchCompletePercentage', label: 'Catch %' },
          //     { accessor: 'avgRouteDistance', label: 'Avg Route Dist', unitType: 'distance' },
          //     { accessor: 'avgWorkRate', label: 'Work Rate', unitType: 'workRate' }
          //   ]
          // },
          // {
          //   key: 'kick',
          //   label: 'Kick',
          //   columns: [
          //     { accessor: 'kickCount', label: 'Kicks', unitType: 'count' },
          //     { accessor: 'goodKickPercentage', label: 'Good %' },
          //     { accessor: 'kickAvgReleaseVelocity', label: 'Release Vel', unitType: 'speed' },
          //     { accessor: 'kickAvgSpin', label: 'Spin', unitType: 'revs' },
          //     { accessor: 'kickAvgSpiral', label: 'Spiral' },
          //     { accessor: 'kickAvgHangTime', label: 'Hang Time', unitType: 'time' },
          //     { accessor: 'kickAvgDistance', label: 'Distance', unitType: 'distance' },
          //     { accessor: 'avgFieldGoalDistance', label: 'FG Dist', unitType: 'distance' },
          //     { accessor: 'kickAvgExecutionTime', label: 'Exec Time', unitType: 'time' }
          //   ]
          // },
          // {
          //   key: 'punt',
          //   label: 'Punt',
          //   columns: [
          //     { accessor: 'puntCount', label: 'Punts', unitType: 'count' },
          //     { accessor: 'puntAvgReleaseVelocity', label: 'Release Vel', unitType: 'speed' },
          //     { accessor: 'puntAvgSpin', label: 'Spin', unitType: 'revs' },
          //     { accessor: 'puntAvgSpiral', label: 'Spiral' },
          //     { accessor: 'puntAvgHangTime', label: 'Hang Time', unitType: 'time' },
          //     { accessor: 'puntAvgDistance', label: 'Distance', unitType: 'distance' },
          //     { accessor: 'avgPuntDistance', label: 'Punt Dist', unitType: 'distance' },
          //     { accessor: 'puntAvgExecutionTime', label: 'Exec Time', unitType: 'time' }
          //   ]
          // }
        ]
      }
    ]
  },

  playerCompare: {
    staticColumns: [
      { accessor: 'playerNumber', label: '#' },
      { accessor: 'playerName', label: 'Player' },
      { accessor: 'playerPosition', label: 'Position' },
      { accessor: 'sessionCount', label: 'Sessions', unitType: 'count' }
    ],
    summaryColumns: [
      { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
      { accessor: 'passCompletionPct', label: 'Complete %' },
      { accessor: 'technicalLoadPerMin', label: 'Load/min' },
      { accessor: 'totalDistance_m', label: 'Distance', unitType: 'distance' },
      { accessor: 'highAccelCount', label: 'Intensity', unitType: 'count' }
    ],
    tabGroups: [
      {
        key: 'physical',
        label: 'Physical',
        tabs: [
          {
            key: 'qb',
            label: 'QB',
            columns: [
              { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
              {
                accessor: 'highVelocityThrowCount',
                label: 'High Vel',
                unitType: 'count'
              },
              { accessor: 'passCompletionPct', label: 'Complete %' },
              {
                accessor: 'initialReleaseVelocityAvg_mps',
                label: 'Release Vel',
                unitType: 'speed'
              },
              { accessor: 'spinAvg_rps', label: 'Spin', unitType: 'revs' },
              {
                accessor: 'timeToThrowAvg_s',
                label: 'Time to Throw',
                unitType: 'time'
              },
              {
                accessor: 'throwDistanceAvg_m',
                label: 'Dist Thrown',
                unitType: 'distance'
              }
            ]
          },
          {
            key: 'wr',
            label: 'WR',
            columns: [
              { accessor: 'routeCount', label: 'Routes', unitType: 'count' },
              { accessor: 'catchCompletePercentage', label: 'Catch %' },
              {
                accessor: 'routePlusPassedToCount',
                label: 'Route + Pass',
                unitType: 'count'
              },
              {
                accessor: 'avgWorkRate',
                label: 'Work Rate',
                unitType: 'workRate'
              },
              {
                accessor: 'avgRouteDistance',
                label: 'Route Dist',
                unitType: 'distance'
              }
            ]
          },
          {
            key: 'kick',
            label: 'Kicker',
            columns: [
              { accessor: 'kickCount', label: 'Kicks', unitType: 'count' },
              { accessor: 'goodKickPercentage', label: 'Good %' },
              {
                accessor: 'kickAvgReleaseVelocity',
                label: 'Release Vel',
                unitType: 'speed'
              },
              { accessor: 'kickAvgSpin', label: 'Spin', unitType: 'revs' },
              {
                accessor: 'kickAvgExecutionTime',
                label: 'Exec Time',
                unitType: 'time'
              },
              {
                accessor: 'kickAvgDistance',
                label: 'Kick Dist',
                unitType: 'distance'
              },
              {
                accessor: 'avgFieldGoalDistance',
                label: 'FG Dist',
                unitType: 'distance'
              }
            ]
          },
          {
            key: 'punt',
            label: 'Punter',
            columns: [
              { accessor: 'puntCount', label: 'Punts', unitType: 'count' },
              {
                accessor: 'puntAvgDistance',
                label: 'Distance',
                unitType: 'distance'
              },
              {
                accessor: 'avgPuntDistance',
                label: 'Punt Dist',
                unitType: 'distance'
              },
              {
                accessor: 'puntAvgReleaseVelocity',
                label: 'Release Vel',
                unitType: 'speed'
              },
              { accessor: 'puntAvgSpin', label: 'Spin', unitType: 'revs' },
              {
                accessor: 'puntAvgExecutionTime',
                label: 'Exec Time',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'physical',
            label: 'Physical',
            columns: [
              {
                accessor: 'totalDistance_m',
                label: 'Distance',
                unitType: 'distance'
              },
              ...speedZoneColumns,
              { accessor: 'sprintCount', label: 'Sprints', unitType: 'count' },
              {
                accessor: 'highIntensityAccelCount',
                label: 'Accels',
                unitType: 'count'
              },
              {
                accessor: 'highIntensityDecelCount',
                label: 'Decels',
                unitType: 'count'
              },
              {
                accessor: 'totalMechanicalWork_kJ',
                label: 'Total Work',
                unitType: 'energy'
              }
            ]
          },
          {
            key: 'qb_technical_load',
            label: 'QB Tech Load',
            columns: [
              { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
              {
                accessor: 'throwCountLt15m',
                label: '<15 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount15mTo30m',
                label: '15-30 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount30mTo45m',
                label: '30-45 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountGt45m',
                label: '>45 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountLt18ms',
                label: '<40 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount18msTo22ms',
                label: '40-50 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountGt22ms',
                label: '>50 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              }
            ]
          }
        ]
      }
    ]
  },

  teamTable: {
    staticColumns: [
      { accessor: 'playerNumber', label: '#' },
      { accessor: 'playerName', label: 'Player' },
      { accessor: 'playerPosition', label: 'Pos' }
    ],
    // Tab order (left → right): QB Pass, Physical, Technical Load,
    // Compare. `all` stays in the codebase (commented out) so it can
    // be re-enabled once the underlying stats ship end-to-end.
    tabGroups: [
      {
        key: 'physical',
        label: 'Physical',
        tabs: [
          {
            key: 'qb_pass',
            label: 'QB Pass',
            columns: [
              { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
              {
                accessor: 'highVelocityThrowCount',
                label: 'High Vel',
                unitType: 'count'
              },
              { accessor: 'passCompletionPct', label: 'Complete %' },
              {
                accessor: 'initialReleaseVelocityAvg_mps',
                label: 'Release Vel',
                unitType: 'speed'
              },
              { accessor: 'spinAvg_rps', label: 'Spin', unitType: 'revs' },
              {
                accessor: 'hangTimeAvg_s',
                label: 'Hang Time',
                unitType: 'time'
              },
              {
                accessor: 'throwDistanceAvg_m',
                label: 'Distance',
                unitType: 'distance'
              },
              {
                accessor: 'airYardsGainedAvg_m',
                label: 'Air Yards',
                unitType: 'distance'
              },
              {
                accessor: 'timeToThrowAvg_s',
                label: 'Time to Throw',
                unitType: 'time'
              }
            ]
          },
          {
            key: 'physical_summary',
            label: 'Physical',
            columns: [
              {
                accessor: 'totalDistance_m',
                label: 'Distance',
                unitType: 'distance'
              },
              ...speedZoneColumns,
              {
                accessor: 'maxSpeed_mps',
                label: 'Max Speed',
                unitType: 'speed'
              },
              {
                accessor: 'highIntensityAccelCount',
                label: 'High Accel Count',
                unitType: 'count'
              },
              {
                accessor: 'highIntensityDecelCount',
                label: 'High Decel Count',
                unitType: 'count'
              }
            ]
          },
          {
            key: 'technical_load',
            label: 'Technical Load',
            columns: [
              { accessor: 'throwCount', label: 'Throws', unitType: 'count' },
              { accessor: 'throwCountPerMin', label: 'Throws/min' },
              {
                accessor: 'throwCountLt15m',
                label: '<15 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount15mTo30m',
                label: '15-30 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount30mTo45m',
                label: '30-45 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountGt45m',
                label: '>45 yd',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountLt18ms',
                label: '<40 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCount18msTo22ms',
                label: '40-50 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              },
              {
                accessor: 'throwCountGt22ms',
                label: '>50 mph',
                unitType: 'count',
                fixedUnitNote:
                  "Threshold fixed in American units; doesn't follow the Units picker"
              }
            ]
          }
          // Disabled — re-enable once the stats are wired end-to-end.
          // {
          //   key: 'all',
          //   label: 'All',
          //   columns: [
          //     { accessor: 'technicalLoad', label: 'Tech Load' },
          //     { accessor: 'technicalLoadPerMin', label: 'Tech Load/min' },
          //     { accessor: 'totalDistance_m', label: 'Distance', unitType: 'distance' },
          //     { accessor: 'fieldYardsGained', label: 'Yards Gained', unitType: 'distance' },
          //     { accessor: 'successPercentage', label: 'Success %' },
          //     { accessor: 'playerLoad', label: 'Player Load' },
          //     { accessor: 'playerLoadPerMin', label: 'Player Load/min' }
          //   ]
          // }
        ]
      }
    ],
    chartCategories: ['Pass', 'Carry']
  },

  monitoring: {
    physicalMetrics: [
      {
        key: 'runningDistance',
        label: 'Distance',
        unitType: 'distance'
      },
      {
        key: 'highSpeedRunningDistance',
        label: 'HSR Distance',
        unitType: 'distance'
      },
      {
        key: 'sprintRunningDistance',
        label: 'Sprint Distance',
        unitType: 'distance'
      },
      { key: 'sprintCount', label: 'Sprints', unitType: 'count' },
      { key: 'highAccelCount', label: 'Accels', unitType: 'count' },
      { key: 'highDecelCount', label: 'Decels', unitType: 'count' },
      { key: 'totalMechanicalWork_kJ', label: 'Total Work', unitType: 'energy' }
    ],
    technicalMetrics: getAmfTechnicalMetrics
  }
}

// Commenting this out for now as it's not needed with the current state of the data
// TODO: We need to readdress this when we modify the keys for the bands
//
// const THROW_DISTANCE_BUCKETS: {
//   accessor: string
//   min: number | null
//   max: number | null
// }[] = [
//   { accessor: 'throwCountLt15m', min: null, max: 15, unitType: 'count' },
//   { accessor: 'throwCount15mTo30m', min: 15, max: 30, unitType: 'count' },
//   { accessor: 'throwCount30mTo45m', min: 30, max: 45, unitType: 'count' },
//   { accessor: 'throwCountGt45m', min: 45, max: null, unitType: 'count' }
// ]

// const THROW_SPEED_BUCKETS: {
//   accessor: string
//   min: number | null
//   max: number | null
// }[] = [
//   { accessor: 'throwCountLt18ms', min: null, max: 18, unitType: 'count' },
//   { accessor: 'throwCount18msTo22ms', min: 18, max: 22, unitType: 'count' },
//   { accessor: 'throwCountGt22ms', min: 22, max: null, unitType: 'count' }
// ]

export class AmericanFootballConfig extends ReportingSportConfig<AmfPosition> {
  constructor(unitSystem: UnitSystem) {
    super(unitSystem, americanFootballData)
  }
}

export { americanFootballData }
